<template>
    <div id="punch-log-page">
        <!-- HEADER -->
        <div class="action-header d-flex-wrap mb-3">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.punchLogAttendance.searchByEmployeeNoOrName')"
                        solo dense hide-details=""
                        height="40px"
                        @keyup.enter="searchPunchLog()">
                    </v-text-field>
                    <v-btn
                        class="ml-3"
                        height="40px"
                        color="primary"
                        outlined
                        @click="searchPunchLog()">
                        {{ $vuetify.lang.t('$vuetify.punchLogAttendance.search') }}
                    </v-btn>
                </div>
            </div>
            <div class="d-flex ml-3">
                <v-btn
                    class="mr-3 btn-text-color"
                    height="50"
                    width="170"
                    @click="genTextFile()"
                    color="#d76436">
                    {{ $vuetify.lang.t('$vuetify.statusAttendance.exportTXT') }}
                </v-btn>
                <v-btn
                    class="mr-3 btn-text-color"
                    height="50"
                    width="170"
                    @click="genPdfReport()"
                    color="#ff544a">
                    {{ $vuetify.lang.t('$vuetify.punchLogAttendance.exportPDF') }}
                </v-btn>
                <v-btn
                    class="btn-text-color"
                    height="50"
                    width="170"
                    @click="genExcelReport()"
                    color="#139888">
                    {{ $vuetify.lang.t('$vuetify.punchLogAttendance.exportExcel') }}
                </v-btn>
            </div>
        </div>
        <div class="action-header">
            <div class="d-flex-wrap">
                <date-picker-range
                    class="mr-3 date-range-input"
                    v-model="dateRange" />
                <div class="mr-3 select-filter">
                    <v-select
                        @change="searchPunchLog()"
                        v-model="bureauFilter"
                        :items="bureauList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        height="40"
                        background-color="#ffffff">
                    </v-select>
                </div>
                <div class="mr-3 select-filter">
                    <v-select
                        @change="searchPunchLog()"
                        v-model="methodFilter"
                        :items="methodList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        height="40"
                        background-color="#ffffff">
                    </v-select>
                </div>
                <div class="select-filter">
                    <v-select
                        @change="searchPunchLog()"
                        v-model="locationFilter"
                        :items="locationList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        height="40"
                        background-color="#ffffff">
                    </v-select>
                </div>
            </div>
        </div>
        <!-- END HEADER -->
        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="totalData"
                :items="items"
                :loading="loading">
                <template v-slot:[`item.time`]="{ item }">
                    <span
                        v-if="item.time && item.time != '00:00'">
                        {{ item.time }}
                    </span>
                    <span v-else>
                        -
                    </span>
                </template>

                <template v-slot:[`item.snap_f_path`]="{ item }">
                    <div class="show-image">
                        <div :class="imageStyle(item)">
                            <i class="far fa-portrait" @click="showImage(item.snap_f_path)"></i>
                        </div>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div
            v-if="pageCount"
            class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>
        <image-preview
            ref="imagePreview" />
    </div>
</template>

<script>
import DatePickerRange from '@/components/DatePickerRange.vue'
import ImagePreview from '@/components/ImagePreview'

export default {
    name: 'punchLog',

    components: {
        DatePickerRange,
        ImagePreview
    },

    data () {
        return {
            loading: false,
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            items: [],
            totalData: 0,
            dateRange: [this.$dateUtil.getCurrentDate(), this.$dateUtil.getCurrentDate()],
            bureauFilter: null,
            methodFilter: null,
            locationFilter: null,
            bureauList: [],
            methodList: [],
            locationList: []
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.employeeId'), value: 'employeeId', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.employeeName'), value: 'fullname', width: '20%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.bureau'), value: 'bureau', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.image'), value: 'snap_f_path', width: '8%', sortable: false, align: 'center' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.method'), value: 'method', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.location'), value: 'device_name', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.date'), value: 'date', width: '10%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.time'), value: 'time', width: '8%' },
                { text: this.$vuetify.lang.t('$vuetify.punchLogAttendance.temperature'), value: 'temperature', width: '8%' }
            ]
        }
    },

    methods: {
        async initPunchLog () {
            await this.getBureaus()
            await this.getMethods()
            await this.getLocations()
            await this.getPunchLog()
        },

        async searchPunchLog () {
            this.options.page = 1
            await this.getPunchLog()
        },

        async getPunchLog () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options
            let url = `punchlogs?page=${page}&itemsPerPage=${itemsPerPage}&type=EMP`

            if (sortBy && sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc && sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            if (this.dateRange.length > 0) {
                url += `&dateRange=${JSON.stringify(this.dateRange)}`
            }

            if (this.bureauFilter) {
                url += `&bureau=${this.bureauFilter}`
            }

            if (this.methodFilter) {
                url += `&method=${this.methodFilter}`
            }

            if (this.locationFilter) {
                url += `&location=${this.locationFilter}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.items = res.data.data
                this.totalData = res.data.total
            }
            this.loading = false
        },

        async getBureaus () {
            const res = await this.axios.get('bureau/options')
            if (res && res.data && res.data.status === 'ok') {
                this.bureauList = res.data.data
            }
        },

        async getMethods () {
            const res = await this.axios.get('punchlog/method/options')
            if (res && res.data && res.data.status === 'ok') {
                this.methodList = res.data.data
            }
        },

        async getLocations () {
            const res = await this.axios.get('punchlog/location/options')
            if (res && res.data && res.data.status === 'ok') {
                this.locationList = res.data.data
            }
        },

        imageStyle (path) {
            return path === null || path === undefined ? 'image-disable' : 'image-enable'
        },

        showImage (path) {
            this.$refs.imagePreview.toggleShow(path)
        },

        async genTextFile () {
            await this.$store.dispatch('setLoading', true)
            const form = {
                search: this.search,
                dateRange: this.dateRange,
                bureau: this.bureauFilter,
                type: 'EMP',
                method: this.methodFilter,
                location: this.locationFilter
            }
            const response = await this.axios.post(
                'punchlog/export/text',
                form,
                {
                    headers: { 'Content-Type': 'text/plain; charset=utf-8' },
                    responseType: 'blob'
                })

            if (response && response.data) {
                const timestamp = Date.now()
                this.$utils.saveLocalFile(`export-attendance-${timestamp}.txt`, response.data)
            } else {
                await this.$store.dispatch('setErrorModalShow', true)
                await this.$store.dispatch('setErrorModalMsg', this.$vuetify.lang.t('$vuetify.statusAttendance.somethingWrong'))
            }
            await this.$store.dispatch('setLoading', false)
        },

        async genPdfReport () {
            const form = {
                search: this.search,
                dateRange: this.dateRange,
                bureau: this.bureauFilter,
                type: 'EMP',
                method: this.methodFilter,
                location: this.locationFilter
            }

            localStorage.formData = JSON.stringify(form)
            const routeData = this.$router.resolve({ name: 'ReportPunchLogPDF' })
            window.open(routeData.href)
        },

        async genExcelReport () {
            await this.$store.dispatch('setLoading', true)
            const response = await this.axios.post(
                'punchlog/export/excel',
                {
                    search: this.search,
                    dateRange: this.dateRange,
                    bureau: this.bureauFilter,
                    type: 'EMP',
                    method: this.methodFilter,
                    location: this.locationFilter
                }, {
                    headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                    responseType: 'arraybuffer'
                })

            if (response && response.data) {
                const dateString = this.$dateUtil.getCurrentDate()
                this.$utils.downloadExcelFile(response.data, `punch-log-report-${dateString}.xlsx`)
            } else {
                await this.$store.dispatch('setErrorModalShow', true)
                await this.$store.dispatch('setErrorModalMsg', this.$vuetify.lang.t('$vuetify.punchLogAttendance.somethingWrong'))
            }
            await this.$store.dispatch('setLoading', false)
        }
    },

    watch: {
        options: {
            async handler () {
                await this.initPunchLog()
            },
            deep: true
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.initPunchLog()
            },
            deep: true
        },
        dateRange () {
            if (this.dateRange.length === 0 || this.dateRange.length === 2) {
                this.searchPunchLog()
            }
        }
    }

}
</script>

<style lang="scss" scoped>
    .search-container {
        ::v-deep .v-input__control > .v-input__slot {
            min-height: auto !important;
            display: flex !important;
            align-items: center !important;
            box-shadow: none !important;
            border: 1px solid #a8a8a8 !important;
            width: 300px;
        }

        ::v-deep .search-input {
            align-items: center;
            .v-input__control > .v-input__slot {
                width: 340px;
            }
        }
    }

    ::v-deep .date-range-input .v-input__control > .v-input__slot {
        width: 220px;
    }

    ::v-deep .select-filter {
        .v-input__control > .v-input__slot {
            border: none !important;
            width: 200px;
        }

        .v-select__selection{
            line-height: 1.75;
        }
    }

    ::v-deep .v-text-field.v-text-field--enclosed .v-text-field__details {
        margin: 0 !important;
    }

    .btn-text-color {
        color: #ffffff;
    }

    .d-flex-wrap {
        display: flex;
        flex-wrap: wrap;
    }

    .show-image {
        align-items: center;

        .image-enable i {
            cursor: pointer;
            color: #d76436 !important;
            font-weight: 500 !important;
            font-size: 16px;
        }

        .image-disable i {
            pointer-events: none;
            cursor: not-allowed;
            color: #a8a8a8 !important;
            font-weight: 300 !important;
            font-size: 16px;
        }
    }

    ::v-deep div[class*="select-filter"] .v-select__selection {
        line-height: 1.75;
    }
</style>
